<template>
  <div class="form-section">
    <h3>Information</h3>
    <div class="form-group">
      <label for="title" class="required">Title</label>
      <input
        type="text"
        id="title"
        v-model="formData.title"
        class="form-control"
        placeholder="Max 80 characters"
        maxlength="80"
        required
      >
    </div>

    <div class="form-group">
      <label for="logline">Logline</label>
      <input
        type="text"
        id="logline"
        v-model="formData.logline"
        class="form-control"
        placeholder="Max 200 characters"
        maxlength="255"
      >
    </div>

    <div class="form-group">
      <label for="description">Description</label>
      <textarea 
        id="description"
        v-model="formData.description"
        class="form-control"
        placeholder="Max 2000 characters"
        rows="4"
      ></textarea>
    </div>

    <div v-if="isSuperuser()" class="form-group">
      <label for="channel" class="required">Channel</label>
      <select 
        id="channel"
        v-model="formData.channel_id"
        class="form-control"
        required
      >
        <option value="">Select a channel</option>
        <option v-for="channel in props.channels" :key="channel.id" :value="channel.id">
          {{ channel.name }}
        </option>
      </select>
    </div>

    <div class="form-group half-width">
      <label for="languages">Languages</label>
      <input
        type="text"
        id="languages"
        v-model="formData.languages"
        class="form-control"
        placeholder="Comma-separated list"
        maxlength="255"
      >
    </div>

    <div class="form-group half-width">
      <label for="subtitles">Subtitles</label>
      <input 
        type="text"
        id="subtitles"
        v-model="formData.subtitles"
        class="form-control"
        placeholder="Comma-separated list"
        maxlength="255"
      >
    </div>

    <div class="form-group full-width">
      <label for="awards">Festival Selections</label>
      <input
        type="text"
        id="awards"
        v-model="formData.awards"
        class="form-control"
        placeholder="Comma-separated list"
        maxlength="255"
      >
    </div>
  </div>


  <div class="form-section">
    <h3>Production</h3>
    <div class="form-group half-width">
      <label for="release_date">First Release Date</label>
      <input
        type="date"
        id="release_date"
        v-model="formData.release_date"
        class="form-control"
      >
    </div>

    <div class="form-group half-width">
      <label for="production_company">Production Companies</label>
      <input 
        type="text"
        id="production_company"
        v-model="formData.production_company"
        class="form-control"
        placeholder="Comma-separated list"
        maxlength="80"
      >
    </div>

    <div class="form-group half-width">
      <label for="directors">Directors</label>
      <input
        type="text"
        id="directors"
        v-model="formData.directors"
        class="form-control"
        placeholder="Comma-separated list"
        maxlength="255"
      >
    </div>

    <div class="form-group half-width">
      <label for="writers">Writers</label>
      <input 
        type="text"
        id="writers"
        v-model="formData.writers"
        class="form-control"
        placeholder="Comma-separated list"
        maxlength="255"
      >
    </div>

    <div class="form-group full-width">
      <label for="starring">Stars</label>
      <input 
        type="text"
        id="starring"
        v-model="formData.starring"
        class="form-control"
        placeholder="Comma-separated list"
        maxlength="255"
      >
    </div>
  </div>

  <div class="form-section">
    <h3>Properties</h3>
    <div class="form-group half-width">
      <label for="category">Category</label>
      <select
        id="category"
        v-model="formData.category"
        class="form-control"
      >
        <option
          v-for="option in categoryOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.display }}
        </option>
      </select>
    </div>

    <div class="form-group half-width">
      <label for="format">Format</label>
      <select
        id="format"
        v-model="formData.format"
        class="form-control"
      >
        <option
          v-for="option in formatOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.display }}
        </option>
      </select>
    </div>

    <div class="form-group half-width">
      <label for="content_rating">ESRB Content Rating</label>
      <select
        id="content_rating"
        v-model="formData.content_rating"
        class="form-control"
      >
        <option
          v-for="option in contentRatingOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.display }}
        </option>
      </select>
    </div>

    <div class="form-group half-width">
      <label for="motion_amount">Motion Amount</label>
      <select
        id="motion_amount"
        v-model="formData.motion_amount"
        class="form-control"
      >
        <option
          v-for="option in motionOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.display }}
        </option>
      </select>
    </div>
  </div>

  <div class="form-section">
    <h3>Assets</h3>
    <div class="form-group half-width">
      <label for="video_file_id">Video File</label>
      <VideoFileSelectField
        v-model="formData.video_file_id"
        :channel-id="formData.channel_id"
      />
    </div>

    <div class="form-group half-width">
      <label for="trailer_file_id">Trailer File</label>
      <VideoFileSelectField
        v-model="formData.trailer_file_id"
        :channel-id="formData.channel_id"
      />
    </div>

    <div class="form-group half-width">
      <label for="banner_url">Hero Art</label>
      <ImageUploadField
        v-model="formData.banner_url"
        folder="distributor_content"
        max-height="400px"
        :req-file-width="2560"
        :req-file-height="1440"
      />
    </div>

    <div class="form-group half-width">
      <label for="poster_url">Poster</label>
      <ImageUploadField
        v-model="formData.poster_url"
        folder="distributor_content"
        max-height="400px"
        :req-file-width="1008"
        :req-file-height="1440"
      />
    </div>
  </div>

  <div v-if="isSuperuser()" class="form-section">
    <h3>Unused Assets</h3>
    <div class="form-group third-width">
      <label for="screenshot1_url">Screenshot 1</label>
      <ImageUploadField
        v-model="formData.screenshot1_url"
        folder="distributor_content"
        max-height="400px"
      />
    </div>

    <div class="form-group third-width">
      <label for="screenshot2_url">Screenshot 2</label>
      <ImageUploadField
        v-model="formData.screenshot2_url"
        folder="distributor_content"
        max-height="400px"
      />
    </div>

    <div class="form-group third-width">
      <label for="screenshot3_url">Screenshot 3</label>
      <ImageUploadField
        v-model="formData.screenshot3_url"
        folder="distributor_content"
        max-height="400px"
      />
    </div>

    <div class="form-group half-width">
      <label for="thumbnail_url">Thumbnail</label>
      <ImageUploadField
        v-model="formData.thumbnail_url"
        folder="distributor_content"
        max-height="400px"
      />
    </div>
  </div>

  <div v-if="isSuperuser()" class="form-section">
    <h3>Admin</h3>
    <div class="form-group">
      <label>Visibility</label>
      <div class="radio-button-group">
        <label 
          v-for="option in visibilityOptions" 
          :key="option.value"
          class="radio-button"
          :class="{ active: formData.visibility === option.value }"
        >
          <input
            type="radio"
            :value="option.value"
            v-model="formData.visibility"
            :name="'visibility'"
          >
          {{ option.display }}
        </label>
        <button 
          type="button" 
          class="action-button delete"
          @click="formData.visibility = ''"
          v-if="formData.visibility"
        >
          <font-awesome-icon icon="trash" />
        </button>
      </div>
    </div>
    
    <div class="form-group">
      <label>Review Status</label>
      <div class="radio-button-group">
        <label 
          v-for="option in reviewStatusOptions" 
          :key="option.value"
          class="radio-button"
          :class="{ active: formData.review === option.value }"
        >
          <input
            type="radio"
            :value="option.value"
            v-model="formData.review"
            :name="'review'"
          >
          {{ option.display }}
        </label>
        <button 
          type="button" 
          class="action-button delete"
          @click="formData.review = ''"
          v-if="formData.review"
        >
          <font-awesome-icon icon="trash" />
        </button>
      </div>
    </div>

    <div class="form-group half-width">
      <label for="avail_start_date">Availability Start Date</label>
      <input 
        id="avail_start_date"
        v-model="formData.avail_start_date"
        type="date"
        class="form-control"
      >
    </div>

    <div class="form-group half-width">
      <label for="avail_end_date">Availability End Date</label>
      <input 
        id="avail_end_date"
        v-model="formData.avail_end_date"
        type="date"
        class="form-control"
      >
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { isSuperuser } from '@/scripts/permissions';
import VideoFileSelectField from '@/components/VideoFileSelectField.vue';
import ImageUploadField from '@/components/ImageUploadField.vue';
import {
  formatOptions,
  categoryOptions,
  motionOptions,
  contentRatingOptions,
  visibilityOptions,
  reviewStatusOptions,
  getDefaultVideoFormData
} from '@/scripts/shared-video';

const props = defineProps({
  channels: {
    type: Array,
    default: () => []
  }
});

const formData = ref(getDefaultVideoFormData());

defineExpose({
    formData
});
</script>

<style scoped>
@import '@/styles/shared-form.css';
</style>
